import { useState } from 'react';
import { Button, Input, Label } from '@dataroom/shared';
import type { BillingInterval } from '@dataroom/shared';
import { useBillingArchivePack, useBillingPricing } from '@/api/hooks';
import { billingErrorMessage, formatUsd } from '@/lib/plan';
import { Modal } from './Modal';

const INTERVALS: { value: BillingInterval; label: string }[] = [
  { value: 'month', label: 'Month to month' },
  { value: 'year', label: 'Billed yearly' },
];

const MAX_PACKS = 100;

interface AddArchivePackModalProps {
  /** Packs the firm already holds: more go on that same subscription, on its interval. */
  currentPacks?: number;
  onClose: () => void;
}

/**
 * "Add archive pack" — each pack keeps 10 more rooms archived on top of the plan
 * (the size and the prices come from the server's catalogue, none is written
 * here). Pick the billing interval and how many packs; owner-only server-side, so
 * a non-owner or a dormant billing shows the plain message instead of a redirect.
 */
export function AddArchivePackModal({ currentPacks = 0, onClose }: AddArchivePackModalProps) {
  const pricing = useBillingPricing();
  const addPack = useBillingArchivePack();
  const [interval, setBillingInterval] = useState<BillingInterval>('month');
  const [packs, setPacks] = useState(1);
  const pack = pricing.data?.archivePack;
  const error = billingErrorMessage(addPack.error);
  const holding = currentPacks > 0;
  const valid = Number.isInteger(packs) && packs >= 1 && packs <= MAX_PACKS;
  const each = pack ? (interval === 'year' ? pack.yearlyCents : pack.monthlyCents) : null;

  return (
    <Modal
      title="Add archive pack"
      description="Extra archived-room capacity on top of your plan — you'll be taken to secure checkout."
      onClose={onClose}
      footer={
        <>
          {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={addPack.isPending}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!pack || !valid || addPack.isPending}
              onClick={() => addPack.mutate({ interval, packs })}
            >
              {addPack.isPending ? 'Redirecting…' : 'Continue to checkout'}
            </Button>
          </div>
        </>
      }
    >
      {pricing.isPending ? (
        <p className="text-sm text-muted-foreground">Loading prices…</p>
      ) : !pack ? (
        <p className="text-sm text-destructive">Prices could not be loaded. Try again shortly.</p>
      ) : (
        <div className="space-y-4">
          {holding ? (
            <p className="rounded-md bg-muted px-3 py-2 text-sm">
              You have {currentPacks} archive {currentPacks === 1 ? 'pack' : 'packs'}. These are
              added to that subscription and bill on its current schedule.
            </p>
          ) : (
            <div
              role="group"
              aria-label="Billing interval"
              className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1"
            >
              {INTERVALS.map((i) => (
                <button
                  key={i.value}
                  type="button"
                  aria-pressed={interval === i.value}
                  onClick={() => setBillingInterval(i.value)}
                  className={`rounded px-2 py-1.5 text-sm font-medium ${
                    interval === i.value ? 'bg-background shadow-sm' : 'text-muted-foreground'
                  }`}
                >
                  {i.label}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="archive-pack-count">Number of packs</Label>
            <Input
              id="archive-pack-count"
              type="number"
              min={1}
              max={MAX_PACKS}
              value={packs}
              onChange={(e) => setPacks(Number(e.target.value))}
            />
          </div>

          <div className="rounded-md border p-3 text-sm">
            <p className="font-medium">
              {valid ? packs * pack.rooms : 0} more archived rooms
            </p>
            {each !== null && valid && !holding ? (
              <p className="text-muted-foreground">
                {formatUsd(each * packs)} {interval === 'year' ? 'billed yearly' : 'per month'} (
                {formatUsd(each)} per pack)
              </p>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
}
